import {
  EditTravelInsurance,
  EntryTypeEnum,
  MedicalMultipleTerm,
  OrderModify,
  RequestMedical,
  SportCategoryEnum,
  TravelCategoryEnum
} from '@insurance/api/requests'
import {
  deviceSession,
  extractSeriesAndNumber,
  formatDateToISOFormat
} from '@shared/utils'
import dayjs from 'dayjs'

import { HealthFormValues } from './types'
import { getContractorFields, getCustomerFields } from '../PersonsFields'

export const INSURED_AFTER_DAYS = 5
export const INSURANCE_DURATION_MIN_DAYS = 1
export const INSURANCE_DURATION_MAX_DAYS = 365
export const MIN_AGE_HEALTH_INSURANCE = 1
export const MIN_AGE = 18
export const MAX_INSURED_AMOUNT = 100000
export const INSURANCE_FORM_DATA_KEY = '__health_insurance_form_data__'

export const normalizeCommonData = (
  values: HealthFormValues,
  isInitPayment = false
): RequestMedical => {
  const isSingleTripType = values.entry_type === EntryTypeEnum.SINGLE
  const hasSportsCategory =
    values.travel_category === TravelCategoryEnum.SPORT_COMPETITIONS &&
    !!values.sport_category
  const session = deviceSession()

  return {
    directions: values.directions || [],
    insurance: values.insurance,
    entry_type: values.entry_type,
    valid_from: formatDateToISOFormat(values.valid_from)!,
    custom_amount: values.custom_amount,

    ...(isSingleTripType && {
      valid_to: formatDateToISOFormat(values.valid_to)
    }),
    ...(!isSingleTripType && {
      days: Number(values.days),
      term: Number(values.term)
    }),
    in_country: !!values.in_country,
    travel_category: values.travel_category,
    ...(hasSportsCategory && {
      sport_category: values.sport_category
    }),
    covid: !!values.covid,
    category: values.category,
    contractor: isInitPayment ? getContractorFields(values) : undefined,
    persons:
      values.persons?.map((person) => ({
        date_of_birth: formatDateToISOFormat(person.date_of_birth)!,
        country: !person.resident ? person.country : undefined,
        resident: person.resident,

        ...(person.document &&
          isInitPayment && {
            document: extractSeriesAndNumber(person.document)!
          }),
        ...(person.identification_number &&
          isInitPayment && {
            identification_number: String(person.identification_number)
          }),
        ...(person.first_name &&
          isInitPayment && {
            first_name: person.first_name
          }),
        ...(person.last_name &&
          isInitPayment && {
            last_name: person.last_name
          })
      })) || [],

    session: {
      device_id: session.deviceId,
      session_id: session.sessionId
    }
  } as RequestMedical
}

export const normalizeEditOrderData = (
  values: HealthFormValues
): EditTravelInsurance => {
  const data = normalizeCommonData(values, true)

  return {
    ...data,
    customer: getCustomerFields(values.customer)
  } as EditTravelInsurance
}

export const normalizeModifyOrderData = (
  values: HealthFormValues
): OrderModify => {
  const data = normalizeEditOrderData(values)

  return {
    medical_travel: {
      effective_date: formatDateToISOFormat(values.effective_date)!,
      insurance: data.insurance,
      covid: data.covid,
      days: data.days || undefined,
      directions: data.directions,
      sport_category: (data.sport_category || undefined) as
        | SportCategoryEnum
        | undefined,
      term: data.term! as MedicalMultipleTerm,
      travel_category: data.travel_category,
      valid_from: data.valid_from,
      valid_to: data.valid_to || undefined,
      entry_type: data.entry_type || undefined,
      customer: data.customer
    }
  }
}

export const saveHealthValuesToStorage = (
  values: HealthFormValues,
  id: string | number = ''
) => {
  const stringifyValues = JSON.stringify(values)

  sessionStorage.setItem(id + INSURANCE_FORM_DATA_KEY, stringifyValues)
}

export const getHealthValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + INSURANCE_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values) as HealthFormValues

  return {
    ...parsedValues,
    valid_from: dayjs(parsedValues.valid_from),
    valid_to: dayjs(parsedValues.valid_to),
    persons: parsedValues.persons?.map((person) => ({
      ...person,
      ...(person?.date_of_birth && {
        date_of_birth: dayjs(person.date_of_birth)
      })
    }))
  }
}

export const removeHealthValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + INSURANCE_FORM_DATA_KEY)
